// PMScheduleBuildModal — the PM sheet's "Schedule build" button (Oct 19, 2026).
//
// Greg: "i know we built out the reschedule build button a few days ago on the
// pm board. is there anyway to use that same functionality when scheduling a
// build?" One confirm does it all on the server (build-calendar `schedule`):
// the build + drop dates and the stage, the Google event (made, or the one
// Robert already made by hand for that date linked), the SRS order, the
// homeowner's "build scheduled" text, Chad's follow-up text, the RM's notice
// and the records. The window then shows each step's result, so a Google,
// email or text failure never looks like success.
//
// Chad's approval (Greg): "Chad's approval is just making sure if the first
// payment has not been received, there is a plan ... if one of those are chosen
// and that is added to the text to the homeowner, allow and skip chad. Chad
// wants a text notification when that is the case so he can follow up." So when
// the first payment isn't in, Robert must pick the plan; it goes in the
// homeowner's text and Chad gets it by text.
//
// The first payment is worked out here with calcScope (the same number the
// homeowner's portal shows), net of every payment recorded on the deal.
//
// Oct 21, 2026 (Greg: "move the gate for initial payment from roberts
// 'schedule build' to submitting the build"): the plan is picked when the build
// is submitted (build_payment_plans), and this window shows it instead of
// asking. It only asks when no plan is on file: a build scheduled straight
// from the install meeting (that submits it), or one submitted before the
// change. The server (build-calendar v7) reads the plan on file itself.
//
// Oct 26, 2026 (Greg: "sometimes builds are 2 days ... can you add start and
// end day. its always an all day"): a first day and a last day, with 1 / 2 / 3
// day buttons. The Google event spans every day, and the homeowner, Chad and
// RM texts name both. A one-day build sends no last day (build-calendar v9).

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { supabase } from "../../../lib/supabase";
import type { Deal } from "../../../lib/types";
import { calcScope } from "../../../lib/scopeCalc";
import type { MaterialOrderSend } from "../../../lib/srsMaterialOrder";
import {
  buildCalendar, fetchCalendarStatus, fmtShortDate, fmtBuildSpan, srsDropDateISO, isWeekendISO, addDaysISO, calendarDayUrl,
  addWeekdaysISO, buildDayCount, buildEndProblem, normalizeBuildEnd,
  type CandidateEvent, type CalendarStatus, type ScheduleResult,
} from "../../../lib/buildCalendar";
import {
  PAYMENT_PLAN_LABELS, choiceIsComplete, fetchCurrentPlan, firstPaymentStatus, planPhrase,
  type BuildPaymentPlan, type PlanChoice,
} from "../../../lib/buildPaymentPlan";
import BuildPaymentPlanPicker from "../../deal/BuildPaymentPlanPicker";
import {
  buildScheduledText, buildPaymentFollowupText, scheduleTextKind, firstNameForText, portalPayLink, homeownerTextTiming, smsInfo, fmtUSD,
} from "../../../../supabase/functions/build-calendar/eventText";

interface Props {
  /** The PM sheet's deal (a lean row; the window reads the full row itself). */
  deal: Deal;
  defaultTitle: string;
  guests: string[];
  location: string;
  buildDescription: (dropDate: string, dropNotes: string, buildDate: string, buildEndDate?: string | null) => string;
  /** The PM sheet list + the deal's build notes, as they go on the event. */
  specialInstructions: string[];
  initialDropDate: string;
  initialDropNotes: string;
  /** The last material order sent for this job, if any. */
  lastOrder: MaterialOrderSend | null;
  /** The CRM already holds this build's Google event. */
  hasCalendarLink: boolean;
  onClose: () => void;
  onDone: (r: ScheduleResult) => void;
}

type FullDeal = Deal & Record<string, unknown>;
type Phase = "form" | "running" | "done";

const STAGE_LABEL: Record<string, string> = {
  install_meeting: "Install Meeting",
  build_submitted: "Build Submitted (waiting on Chad)",
  schedule_build: "Schedule Build",
  build_scheduled: "Build Scheduled",
};

/** Eastern date + hour (the server's clock for past dates and quiet hours). */
function etNow(): { date: string; hour: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date());
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return { date: `${g("year")}-${g("month")}-${g("day")}`, hour: Number(g("hour")) % 24 };
}

/** "Tue, Oct 20, 10:14 AM" Eastern. */
function fmtTexted(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
  });
}

function nextWeekdaysFrom(fromISO: string, n: number): string[] {
  const out: string[] = [];
  let cur = fromISO;
  while (out.length < n) {
    cur = addDaysISO(cur, 1);
    if (!isWeekendISO(cur)) out.push(cur);
  }
  return out;
}

const n = (v: unknown) => Number(v ?? 0) || 0;

export default function PMScheduleBuildModal(props: Props) {
  const { deal, lastOrder, onClose, onDone } = props;
  const today = useMemo(() => etNow().date, []);
  const futureOrNull = (iso: string | null | undefined) => {
    const d = iso ? iso.slice(0, 10) : "";
    return d && d >= today && !isWeekendISO(d) ? d : "";
  };
  const orderSent = !!lastOrder && lastOrder.send_status === "sent";
  const initialBuild = futureOrNull(deal.build_scheduled_date) || (orderSent ? futureOrNull(lastOrder!.build_date) : "");
  const initialDrop =
    (orderSent && lastOrder!.build_date === initialBuild && lastOrder!.drop_date) ||
    props.initialDropDate ||
    (initialBuild ? srsDropDateISO(initialBuild) : "");

  const initialEnd = initialBuild && deal.build_scheduled_date?.slice(0, 10) === initialBuild
    ? (normalizeBuildEnd(initialBuild, deal.build_end_date) ?? "") : "";

  // ── form state ──
  const [buildDate, setBuildDate] = useState(initialBuild);
  // The last day; "" = a one-day build.
  const [buildEnd, setBuildEnd] = useState(initialEnd);
  const [dropDate, setDropDate] = useState(initialDrop);
  const [dropTouched, setDropTouched] = useState(false);
  const [dropNotes, setDropNotes] = useState(props.initialDropNotes || "Early AM");
  const [title, setTitle] = useState(props.defaultTitle);
  // Asked only when no plan was recorded at submission (see the header).
  const [choice, setChoice] = useState<PlanChoice>({ plan: "", note: "" });
  const [sendSrs, setSendSrs] = useState(true);
  const [srsTouched, setSrsTouched] = useState(false);
  const [textHomeowner, setTextHomeowner] = useState(true);
  const [textTouched, setTextTouched] = useState(false);
  const [calChoice, setCalChoice] = useState<string>("");   // "" = automatic, "new", or an event id

  // ── loaded state ──
  const [onFile, setOnFile] = useState<BuildPaymentPlan | null>(null);
  const [onFileBy, setOnFileBy] = useState<string | null>(null);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [full, setFull] = useState<FullDeal | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [calStatus, setCalStatus] = useState<CalendarStatus | null | "loading">("loading");
  const [candidates, setCandidates] = useState<CandidateEvent[]>([]);

  const [phase, setPhase] = useState<Phase>("form");
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ScheduleResult | null>(null);

  // The full deal row: scope numbers, payments, phone, opt-out, agreement.
  useEffect(() => {
    let alive = true;
    supabase.from("deals").select("*").eq("id", deal.id).maybeSingle().then(({ data, error }) => {
      if (!alive) return;
      if (error || !data) setLoadErr(error?.message ?? "Couldn't load this deal.");
      else setFull(data as FullDeal);
    });
    fetchCalendarStatus().then((s) => { if (alive) setCalStatus(s); }).catch(() => { if (alive) setCalStatus(null); });
    // The plan recorded when the build was submitted, and who recorded it.
    fetchCurrentPlan(deal.id)
      .then(async (p) => {
        if (!alive) return;
        setOnFile(p);
        if (p?.actor_id) {
          const { data: who } = await supabase.from("team_roster").select("full_name").eq("id", p.actor_id).maybeSingle();
          if (alive) setOnFileBy((who?.full_name as string | undefined) ?? null);
        }
      })
      .catch(() => { if (alive) setOnFile(null); })
      .finally(() => { if (alive) setPlanLoaded(true); });
    return () => { alive = false; };
  }, [deal.id]);
  const calConfigured = calStatus !== "loading" && !!calStatus?.configured;

  // The automatic build text (homeowner-comms) may already have told them a
  // date. On that same date the server sends only the payment line, or nothing
  // (build-calendar v6), and the preview below shows the same.
  const [engineTold, setEngineTold] = useState<{ at: string; date: string } | null>(null);
  useEffect(() => {
    let alive = true;
    supabase.from("homeowner_comm_sends").select("sent_at,rendered")
      .eq("deal_id", deal.id).eq("comm_key", "build_date").in("status", ["sent", "partial"])
      .not("sent_at", "is", null).order("sent_at", { ascending: false }).limit(1)
      .then(({ data }) => {
        if (!alive) return;
        const row = data?.[0] as { sent_at: string; rendered: Record<string, unknown> | null } | undefined;
        const told = row?.rendered?.told_date;
        setEngineTold(row && typeof told === "string" ? { at: row.sent_at, date: told } : null);
      });
    return () => { alive = false; };
  }, [deal.id]);

  // A multi-day build keeps its length when the first day moves.
  const dayCount = buildDate ? buildDayCount(buildDate, buildEnd || null) : 1;
  function pickStart(d: string) {
    setBuildDate(d);
    setBuildEnd(d && dayCount > 1 ? addWeekdaysISO(d, dayCount - 1) : "");
  }
  function pickDays(n: number) {
    if (!buildDate) return;
    setBuildEnd(n > 1 ? addWeekdaysISO(buildDate, n - 1) : "");
  }
  const endISO = buildDate ? normalizeBuildEnd(buildDate, buildEnd || null) : null;
  const endProblem = buildDate && buildEnd ? buildEndProblem(buildDate, buildEnd) : null;

  // Drop trails the build date (the weekday before) until Robert edits it.
  const firstDropRun = useRef(true);
  useEffect(() => {
    if (firstDropRun.current) { firstDropRun.current = false; return; }
    if (!dropTouched) setDropDate(buildDate ? srsDropDateISO(buildDate) : "");
  }, [buildDate, dropTouched]);

  // SRS: on unless an order already went out for these same dates.
  const sameOrder = orderSent && lastOrder!.build_date === buildDate && lastOrder!.drop_date === dropDate;
  useEffect(() => { if (!srsTouched) setSendSrs(!sameOrder); }, [sameOrder, srsTouched]);

  // Hand-made events near the date (only when the CRM doesn't hold the event).
  useEffect(() => {
    if (props.hasCalendarLink || !calConfigured || !buildDate) { setCandidates([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      buildCalendar<{ ok: boolean; candidates: CandidateEvent[] }>({ action: "find_candidates", deal_id: deal.id, around_date: buildDate })
        .then((r) => {
          if (!alive) return;
          const near = (r.candidates ?? []).filter((c) => !c.startDate || Math.abs(Date.parse(c.startDate) - Date.parse(buildDate)) <= 3 * 86400000);
          setCandidates(near);
          const same = near.filter((c) => c.startDate === buildDate);
          setCalChoice(same.length === 1 ? same[0].id : near.length ? "new" : "");
        })
        .catch(() => { if (alive) setCandidates([]); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [buildDate, calConfigured, props.hasCalendarLink, deal.id]);

  // ── first payment ──
  const money = useMemo(() => {
    if (!full) return null;
    const isRetail = full.is_retail === true;
    const label = isRetail ? "50% deposit" : "first payment";
    const scopeKnown = isRetail ? n(full.retail_contract_total) > 0 : n(full.rcv_total) > 0;
    const paid = n(full.pre_build_amount_received) + n(full.pre_build_amount_in_flight)
      + n(full.post_build_amount_received) + n(full.post_build_amount_in_flight);
    if (!scopeKnown) return { label, owed: null as number | null, paid, isIn: false };
    const due = calcScope(full).firstPaymentToBardin;
    const owed = Math.max(0, Math.round((due - paid) * 100) / 100);
    return { label, owed, paid, isIn: owed <= 0.5 };
  }, [full]);
  const paymentIn = !!money?.isIn;

  // ── homeowner text ──
  const phoneOk = !!(full?.homeowner_phone && String(full.homeowner_phone).replace(/\D/g, "").length >= 10);
  const optedOut = full?.sms_opt_out === true;
  const unsigned = !!full && full.install_agreement_signed !== true;
  const canText = phoneOk && !optedOut;
  useEffect(() => {
    if (!full || textTouched) return;
    setTextHomeowner(canText && !unsigned);
  }, [full, canText, unsigned, textTouched]);

  // The plan the text and Chad get: the one on file, else the one picked here.
  const plan = onFile?.plan ?? (choice.plan || null);
  const planNote = onFile ? onFile.note : (choice.note.trim() || null);
  const payment = useMemo(() => (!full || paymentIn || !plan) ? null : {
    plan, amount: money?.owed ?? null, label: money?.label ?? "first payment",
    payLink: plan === "online" ? portalPayLink(full.id, full.homeowner_name, full.property_street) : null,
  }, [full, paymentIn, plan, money]);
  const pickerSnap = useMemo(
    () => (full ? { deal: full as Record<string, unknown>, status: firstPaymentStatus(full as Record<string, unknown>), onFile: null, onFileBy: null } : null),
    [full],
  );
  // The automatic text only ever named the first day, so it doesn't cover a multi-day build.
  const toldAt = !endISO && engineTold && engineTold.date === buildDate ? engineTold.at : null;
  const textKind = scheduleTextKind(toldAt, payment !== null);
  const textPreview = useMemo(() => {
    if (!full || !buildDate) return "";
    if (!paymentIn && !plan) return "";
    if (textKind === "already_texted") return "";
    const firstName = firstNameForText(full.homeowner_name);
    return textKind === "payment_only"
      ? buildPaymentFollowupText({ firstName, buildDate, buildEndDate: endISO, payment: payment! })
      : buildScheduledText({ firstName, street: full.property_street, buildDate, buildEndDate: endISO, payment });
  }, [full, buildDate, endISO, plan, paymentIn, payment, textKind]);
  const textInfo = textPreview ? smsInfo(textPreview) : null;
  const et = etNow();
  const textWaits = !!buildDate && homeownerTextTiming(et.hour, et.date, buildDate) === "morning";

  // ── validation ──
  const weekend = !!buildDate && isWeekendISO(buildDate);
  const past = !!buildDate && buildDate < today;
  const dropAfter = !!buildDate && !!dropDate && dropDate > buildDate;
  const stage = String(full?.stage ?? deal.stage ?? "");
  const planReady = paymentIn || (planLoaded && (!!onFile || choiceIsComplete(choice)));
  const canSubmit = phase === "form" && !!full && !!buildDate && !weekend && !past && !endProblem && !dropAfter && !!title.trim() && planReady;

  const description = useMemo(
    () => (buildDate ? props.buildDescription(dropDate, dropNotes, buildDate, endISO) : ""),
    [buildDate, endISO, dropDate, dropNotes, props],
  );

  async function submit() {
    setErr(null);
    if (!buildDate) return setErr("Pick the build date.");
    if (weekend) return setErr("Builds aren't scheduled on weekends. Pick a weekday.");
    if (past) return setErr("That date has already passed.");
    if (endProblem) return setErr(endProblem);
    if (!paymentIn && !onFile && !choiceIsComplete(choice)) {
      return setErr(choice.plan === "other" ? "Type the reason for the Other plan." : "Pick how the first payment will be collected.");
    }
    setPhase("running");
    try {
      const r = await buildCalendar<ScheduleResult>({
        action: "schedule",
        deal_id: deal.id,
        build_date: buildDate,
        build_end_date: endISO,
        drop_date: dropDate || undefined,
        drop_notes: dropNotes,
        event: { title: title.trim(), description, location: props.location, guests: props.guests },
        // With a plan on file the server uses it; only a plan picked here is sent.
        first_payment: {
          due: paymentIn ? 0 : (money?.owed ?? null),
          plan: paymentIn || onFile ? null : (choice.plan || null),
          note: paymentIn || onFile ? null : (choice.note.trim() || null),
          label: money?.label ?? "first payment",
        },
        send_srs: sendSrs,
        text_homeowner: textHomeowner && canText,
        notify_guests: true,
        ...(calChoice && calChoice !== "new" ? { calendar_event_id: calChoice } : {}),
        ...(calChoice === "new" ? { calendar_create_new: true } : {}),
      });
      setResult(r);
      setPhase("done");
      onDone(r);
    } catch (e) {
      setErr((e as Error).message);
      setPhase("form");
    }
  }

  /** The Google template link, for when the calendar isn't connected. */
  function templateUrl(): string {
    const dateStr = buildDate.replace(/-/g, "");
    const endStr = addDaysISO(endISO ?? buildDate, 1).replace(/-/g, "");
    const params = new URLSearchParams({ action: "TEMPLATE", text: title, dates: `${dateStr}/${endStr}`, details: description, location: props.location });
    const guestParams = props.guests.map((e) => `add=${encodeURIComponent(e)}`).join("&");
    return `https://calendar.google.com/calendar/render?${params.toString()}&${guestParams}`;
  }

  const close = () => { if (phase !== "running") onClose(); };
  const chips = useMemo(() => nextWeekdaysFrom(today, 6), [today]);
  const hoPref = futureOrNull(deal.ho_preferred_build_date);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={close} role="dialog" aria-modal="true" aria-label="Schedule build">
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-bardin-ink">{phase === "done" ? "Build scheduled" : "Schedule build"}</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {deal.homeowner_name ?? "(no name)"} · {deal.property_street ?? ""}{stage ? ` · ${STAGE_LABEL[stage] ?? stage}` : ""}
            </p>
          </div>
          <button onClick={close} aria-label="Close" className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        {/* body */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {phase !== "done" && (
            <>
              {loadErr && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{loadErr}</div>}

              {/* heads-up */}
              {full && (unsigned || money?.owed === null) && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 space-y-1">
                  {unsigned && <div>⚠ The homeowner hasn't signed the install agreement yet. The homeowner text starts unticked.</div>}
                  {money && money.owed === null && <div>No scope is entered, so the first payment amount is unknown. Pick the plan anyway.</div>}
                </div>
              )}

              {/* build dates: first day + last day (all day, no times) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Build dates</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {hoPref && !chips.includes(hoPref) && (
                    <DateChip iso={hoPref} on={buildDate === hoPref} onClick={() => pickStart(hoPref)} tag="HO asked" />
                  )}
                  {chips.map((d) => (
                    <DateChip key={d} iso={d} on={buildDate === d} onClick={() => pickStart(d)} tag={d === hoPref ? "HO asked" : undefined} />
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-0.5">First day</label>
                    <input type="date" value={buildDate} min={today} onChange={(e) => pickStart(e.target.value)} className="w-full border border-slate-300 rounded px-3 py-2 text-sm" aria-label="First build day" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-0.5">Last day</label>
                    <input
                      type="date"
                      value={buildEnd || buildDate}
                      min={buildDate || today}
                      disabled={!buildDate}
                      onChange={(e) => setBuildEnd(e.target.value && e.target.value !== buildDate ? e.target.value : "")}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50"
                      aria-label="Last build day"
                    />
                  </div>
                </div>
                <DayButtons count={dayCount} disabled={!buildDate} onPick={pickDays} />
                {weekend && <p className="text-xs text-red-600 mt-1">No weekend builds. Pick a weekday.</p>}
                {past && <p className="text-xs text-red-600 mt-1">That date has already passed.</p>}
                {endProblem && <p className="text-xs text-red-600 mt-1">{endProblem}</p>}
                {buildDate && !weekend && !past && !endProblem && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    {dayCount === 1 ? "One day" : `${dayCount} days`}: {fmtBuildSpan(buildDate, endISO)}{endISO ? ". The calendar event covers every day." : ""}
                  </p>
                )}
              </div>

              {/* drop */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Material drop</label>
                  <input type="date" value={dropDate} onChange={(e) => { setDropTouched(true); setDropDate(e.target.value); }} className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                  {dropAfter
                    ? <p className="text-[11px] text-red-600 mt-1">The drop has to be on or before the build date.</p>
                    : <p className="text-[11px] text-slate-400 mt-1">The weekday before the first build day unless you change it.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Drop notes</label>
                  <input type="text" value={dropNotes} onChange={(e) => setDropNotes(e.target.value)} placeholder="Early AM" className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                </div>
              </div>

              {/* first payment: the plan from the submission, or asked here when none */}
              {full && !paymentIn && planLoaded && !onFile ? (
                <BuildPaymentPlanPicker
                  snap={pickerSnap}
                  value={choice}
                  onChange={setChoice}
                  forcePlan
                  intro="No plan was recorded when this build was submitted."
                />
              ) : (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {money?.label === "50% deposit" ? "50% deposit" : "First payment"}
                  </div>
                  <div className="px-3 py-2.5 text-sm space-y-2">
                    {!full || (!paymentIn && !planLoaded) ? (
                      <div className="text-slate-400 text-xs">Loading the payments…</div>
                    ) : paymentIn ? (
                      <div className="text-emerald-700">✓ In: {fmtUSD(money!.paid)} recorded on the deal. No plan needed.</div>
                    ) : onFile ? (
                      <>
                        <div className="text-slate-700">
                          {money?.owed != null
                            ? <><span className="font-semibold">{fmtUSD(money.owed)}</span> not in yet{money.paid > 0 ? ` (${fmtUSD(money.paid)} recorded so far)` : ""}.</>
                            : <>Not in yet (amount unknown).</>}
                        </div>
                        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
                          <div>
                            <span className="text-slate-500">Plan from the build submission:</span>{" "}
                            <span className="font-semibold">{PAYMENT_PLAN_LABELS[onFile.plan]}</span>
                          </div>
                          {onFile.note && <div className="mt-0.5 text-slate-600">"{onFile.note}"</div>}
                          <div className="mt-0.5 text-slate-400">
                            {new Date(onFile.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" })}
                            {onFileBy ? ` · ${onFileBy}` : ""}
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {/* special instructions */}
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Special instructions on the calendar event ({props.specialInstructions.length})
                </div>
                <div className="px-3 py-2.5 text-sm">
                  {props.specialInstructions.length ? (
                    <ul className="list-disc pl-5 space-y-0.5 text-slate-700">
                      {props.specialInstructions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  ) : (
                    <div className="text-xs text-slate-400">None on this job.</div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-2">The sheet's list plus the deal's build notes. To change one, close this, edit the sheet, then schedule.</p>
                </div>
              </div>

              {/* calendar */}
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Calendar event title</label>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-medium" />
                </div>
                {candidates.length > 0 && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-slate-700 space-y-1.5">
                    <div className="font-semibold text-blue-900">Already on Google near this date</div>
                    {candidates.map((c) => (
                      <label key={c.id} className="flex items-start gap-2">
                        <input type="radio" name="calchoice" checked={calChoice === c.id} onChange={() => setCalChoice(c.id)} className="mt-0.5" />
                        <span className="min-w-0">
                          Update this one: <span className="font-medium">{c.summary}</span>{c.startDate ? ` · ${fmtShortDate(c.startDate)}` : ""}
                          {c.htmlLink && <> · <a href={c.htmlLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">open ↗</a></>}
                        </span>
                      </label>
                    ))}
                    <label className="flex items-start gap-2">
                      <input type="radio" name="calchoice" checked={calChoice === "new"} onChange={() => setCalChoice("new")} className="mt-0.5" />
                      <span>Make a new event</span>
                    </label>
                  </div>
                )}
                <details className="text-xs text-slate-600">
                  <summary className="cursor-pointer select-none">Guests ({props.guests.length}) and the event description</summary>
                  <div className="mt-2 border rounded px-3 py-2 bg-slate-50 space-y-0.5 max-h-28 overflow-y-auto">
                    {props.guests.map((g) => <div key={g}>{g}</div>)}
                  </div>
                  <pre className="mt-2 border rounded px-3 py-2 text-[10px] bg-slate-50 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">{description || "Pick a build date to see it."}</pre>
                </details>
                {calStatus !== "loading" && !calConfigured && (
                  <p className="text-xs text-amber-700">Google Calendar isn't connected, so you'll get a button to add the event by hand after scheduling.</p>
                )}
              </div>

              {/* SRS + homeowner text */}
              <div className="space-y-2 text-sm">
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={sendSrs} onChange={(e) => { setSrsTouched(true); setSendSrs(e.target.checked); }} className="accent-emerald-600 mt-1" />
                  <span>
                    Send the material order to SRS
                    {orderSent && (
                      <span className="block text-xs text-slate-400">
                        Already sent {new Date(lastOrder!.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} for build {fmtShortDate(lastOrder!.build_date)}
                        {sameOrder ? ". Leave it off unless the materials changed." : ". It goes as a DATE CHANGE replacing that order."}
                      </span>
                    )}
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={textHomeowner && canText} disabled={!canText} onChange={(e) => { setTextTouched(true); setTextHomeowner(e.target.checked); }} className="accent-emerald-600 mt-1" />
                  <span>
                    Text the homeowner
                    {!full ? null : optedOut
                      ? <span className="block text-xs text-slate-400">They replied STOP, so no texts.</span>
                      : !phoneOk
                        ? <span className="block text-xs text-slate-400">No phone on file.</span>
                        : textWaits
                          ? <span className="block text-xs text-slate-400">It's past 9 PM or before 8 AM, so it goes out at 8 AM.</span>
                          : null}
                  </span>
                </label>
                {textHomeowner && canText && (
                  textPreview ? (
                    <div className="rounded-xl bg-slate-100 px-3 py-2.5 text-[13px] text-slate-800 leading-snug">
                      {textKind === "payment_only" && toldAt && (
                        <div className="text-[11px] text-slate-500 mb-1.5">
                          They already got the automatic build text for this date ({fmtTexted(toldAt)}), so this sends only the payment line:
                        </div>
                      )}
                      {textPreview}
                      {textInfo && (
                        <div className="text-[10px] text-slate-400 mt-1.5">
                          {textInfo.chars} characters · {textInfo.segments} text{textInfo.segments === 1 ? "" : "s"}{textInfo.gsm7 ? "" : " · has special characters"}
                        </div>
                      )}
                    </div>
                  ) : textKind === "already_texted" && toldAt && paymentIn ? (
                    <p className="text-xs text-slate-500">
                      They already got the automatic build text for this date ({fmtTexted(toldAt)}), so no text goes out.
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400">{!buildDate ? "Pick a build date to see the text." : "Pick how the first payment will be collected to see the text."}</p>
                  )
                )}
              </div>

              <p className="text-xs text-slate-500">
                Puts the build on Team Alex Builds and invites the guests. The RM gets an in-portal notice{!paymentIn ? ", and Chad gets the payment plan by text" : ""}.
              </p>

              {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>}
            </>
          )}

          {phase === "done" && result && (
            <div className="space-y-2.5 text-sm">
              <StepRow ok label="Deal updated">
                Build {fmtBuildSpan(result.deal.build_date, result.deal.build_end_date)} · drop {fmtShortDate(result.deal.drop_date)}{result.deal.drop_notes ? ` (${result.deal.drop_notes})` : ""}
                {result.deal.stage_before !== "build_scheduled" && (
                  <> · moved from {STAGE_LABEL[result.deal.stage_before] ?? result.deal.stage_before} to Build Scheduled{result.deal.skipped_approval ? " (Chad's approval skipped)" : ""}</>
                )}
              </StepRow>

              {plan && !paymentIn && (
                <StepRow ok label="First payment plan">
                  {planPhrase(plan, planNote)}
                  {onFile ? " (recorded when the build was submitted)" : " (recorded now)"}
                </StepRow>
              )}

              <CalendarStep result={result} templateUrl={templateUrl} />

              <StepRow ok={result.srs.status === "sent"} warn={result.srs.status === "failed" || result.srs.status === "empty_order"} label="SRS order">
                {result.srs.status === "sent" && <>Sent{result.srs.subject ? <span className="text-slate-400"> · {result.srs.subject}</span> : null}</>}
                {result.srs.status === "already_sent" && <>Not sent again (already sent {result.srs.prior_sent_at ? new Date(result.srs.prior_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""})</>}
                {result.srs.status === "skipped" && "Not sent (unticked)"}
                {result.srs.status === "empty_order" && "Not sent: no material quantities on the PM sheet yet. Fill them in, then use Calendar / resend order."}
                {result.srs.status === "failed" && <>Didn't go out: {result.srs.error ?? "unknown error"}. Use Calendar / resend order on the sheet.</>}
              </StepRow>

              <StepRow ok={result.homeowner_text.status === "sent" || result.homeowner_text.status === "queued" || result.homeowner_text.status === "already_texted"} warn={result.homeowner_text.status === "failed"} label="Homeowner text">
                {result.homeowner_text.status === "sent" && (result.homeowner_text.payment_only ? "Sent the payment line (they already had the build text)" : "Sent")}
                {result.homeowner_text.status === "already_texted" && <>Not sent again: they got the automatic build text {result.homeowner_text.texted_at ? fmtTexted(result.homeowner_text.texted_at) : "earlier"}</>}
                {result.homeowner_text.status === "queued" && <>Goes out at {result.homeowner_text.send_at ? new Date(result.homeowner_text.send_at).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) : "8 AM"}</>}
                {result.homeowner_text.status === "skipped" && "Not sent (unticked)"}
                {result.homeowner_text.status === "not_sent" && <>Not sent: {result.homeowner_text.reason === "opted_out" ? "they replied STOP" : "no phone on file"}</>}
                {result.homeowner_text.status === "failed" && <>Couldn't send: {result.homeowner_text.error}</>}
              </StepRow>

              <StepRow ok={result.chad.status === "texted" || result.chad.status === "notified" || result.chad.status === "skipped" || result.chad.status === "self"} warn={result.chad.status === "failed"} label="Chad">
                {result.chad.status === "texted" && "Texted the payment plan"}
                {result.chad.status === "notified" && <>In-portal notice only{result.chad.error ? ` (${result.chad.error})` : " (no phone on file)"}</>}
                {result.chad.status === "skipped" && "Nothing to follow up: the first payment is in"}
                {result.chad.status === "self" && "You're Chad, so no text"}
                {result.chad.status === "no_chad" && "Chad isn't on the active roster"}
                {result.chad.status === "failed" && <>Couldn't notify: {result.chad.error}</>}
              </StepRow>

              <StepRow ok={result.rm.notified > 0} label="RM notified">
                {result.rm.notified > 0 ? `${result.rm.notified} in-portal notice${result.rm.notified === 1 ? "" : "s"}` : "No RM on the deal to notify"}
              </StepRow>
              {result.trades && result.trades.length > 0 && (
                <StepRow ok={false} warn label="Trades on this job">
                  {result.trades.map((t) => (
                    <span key={t.id} className="block">
                      {t.work}{t.vendor ? ` (${t.vendor})` : ""}{t.date ? ` on ${fmtShortDate(t.date)}` : ""} no longer fits: {t.reason}
                    </span>
                  ))}
                  <span className="block mt-0.5">Flagged on the Builds board's Trades view. Nothing was moved on Other Trades.</span>
                </StepRow>
              )}
            </div>
          )}
        </div>

        {/* footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2 rounded-b-xl">
          {phase === "done" ? (
            <button onClick={onClose} className="px-4 py-1.5 rounded bg-slate-800 text-white text-sm font-medium hover:bg-slate-900">Done</button>
          ) : (
            <>
              <button onClick={close} disabled={phase === "running"} className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">Cancel</button>
              <button onClick={submit} disabled={!canSubmit} className="px-4 py-1.5 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {phase === "running" ? "Scheduling…" : "Schedule build"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** 1 / 2 / 3 day buttons: they set the last day that many weekdays on. */
function DayButtons({ count, disabled, onPick }: { count: number; disabled: boolean; onPick: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1.5 mt-2">
      <span className="text-[11px] text-slate-500 mr-0.5">How long:</span>
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onPick(n)}
          className={"px-2.5 py-1 rounded-full border text-xs font-medium disabled:opacity-50 " + (count === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}
        >
          {n === 1 ? "1 day" : `${n} days`}
        </button>
      ))}
    </div>
  );
}

function DateChip({ iso, on, onClick, tag }: { iso: string; on: boolean; onClick: () => void; tag?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={"px-2.5 py-1 rounded-full border text-xs font-medium " + (on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}
    >
      {fmtShortDate(iso)}{tag ? <span className={on ? "text-slate-300" : "text-orange-600"}> · {tag}</span> : null}
    </button>
  );
}

function StepRow({ ok, warn = false, label, children }: { ok: boolean; warn?: boolean; label: string; children: ReactNode }) {
  const tone = ok ? "text-emerald-700" : warn ? "text-rose-700" : "text-amber-700";
  const mark = ok ? "✓" : warn ? "✕" : "–";
  return (
    <div className="flex gap-2 items-start">
      <span className={"w-4 shrink-0 font-bold " + tone}>{mark}</span>
      <div className="min-w-0">
        <div className="font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-600">{children}</div>
      </div>
    </div>
  );
}

function CalendarStep({ result, templateUrl }: { result: ScheduleResult; templateUrl: () => string }) {
  const c = result.calendar;
  const link = c.html_link ? <a href={c.html_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open in Google ↗</a> : null;
  if (c.status === "created" || c.status === "updated" || c.status === "adopted") {
    return (
      <StepRow ok label="Google Calendar">
        {c.status === "created" ? "Event created on Team Alex Builds, guests invited" : c.status === "adopted" ? "Found the event you already made for this date and updated it" : "Event updated, guests notified"} {link}
      </StepRow>
    );
  }
  const handLink = (
    <button type="button" onClick={() => window.open(templateUrl(), "_blank")} className="text-blue-600 hover:underline">
      Add it in Google by hand ↗
    </button>
  );
  if (c.status === "not_configured") {
    return <StepRow ok={false} label="Google Calendar">Not connected, so the event wasn't made. {handLink}</StepRow>;
  }
  if (c.status === "error") {
    return (
      <StepRow ok={false} warn label="Google Calendar">
        Google refused it: {c.error ?? "unknown error"}. {handLink}
 · <a href={c.day_url || calendarDayUrl(result.deal.build_date)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">open that day ↗</a>
      </StepRow>
    );
  }
  return <StepRow ok={false} label="Google Calendar">Skipped</StepRow>;
}
